import { differenceInCalendarDays, parseISO, subDays } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import type {
  CohortConfig,
  CohortDate,
  CohortWindow,
  DeadlineUrgency,
  EnrollmentStateResult,
} from '../types'

export const cohortDateToUtc = (
  date: CohortDate,
  timezone: CohortConfig['timezone'],
): Date => zonedTimeToUtc(`${date}T00:00:00`, timezone)

const getUrgency = (
  window: CohortWindow,
  timezone: CohortConfig['timezone'],
  now: Date,
): DeadlineUrgency => {
  const lastDay = subDays(parseISO(window.endsOn), 1)
  const daysLeft = differenceInCalendarDays(
    lastDay,
    utcToZonedTime(now, timezone),
  )

  if (daysLeft === 0) return 'today'
  if (daysLeft === 1) return 'tomorrow'
  return 'soon'
}

export const getWindowState = (
  window: CohortWindow,
  timezone: CohortConfig['timezone'],
  now: Date,
): EnrollmentStateResult => ({
  state: window.windowType,
  deadline: cohortDateToUtc(window.endsOn, timezone).toISOString(),
  urgency: getUrgency(window, timezone, now),
})

const findWindow = (
  config: CohortConfig,
  now: Date,
): CohortWindow | undefined => {
  const nowMs = now.getTime()
  const [firstWindow] = config.windows

  if (
    firstWindow &&
    nowMs < cohortDateToUtc(firstWindow.startsOn, config.timezone).getTime()
  ) {
    return firstWindow
  }

  return config.windows.find(
    (window) =>
      nowMs < cohortDateToUtc(window.endsOn, config.timezone).getTime(),
  )
}

export const getEnrollmentState = (
  config: CohortConfig,
  now: Date,
): EnrollmentStateResult => {
  if (config.soldOutOverride) {
    return { state: 'soldOut' }
  }

  const window = findWindow(config, now)

  if (!window) {
    return { state: 'closed' }
  }

  return getWindowState(window, config.timezone, now)
}
